import { Injector } from '@angular/core';
import { DataGridTemplate } from '../../../../shared/data-grid/data-grid.template';
import { FormGroup, FormBuilder } from '@angular/forms';
import { PartService } from '../../../phf/phf.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { CgpAlertDialogService } from '../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { UploadService } from './upload-service';
import { InvalidColumnsGrid } from './invalid-columns-grid';
import { FilterDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';

export class PartGrid extends DataGridTemplate {
  private readonly formBuilder: FormBuilder;
  private readonly partService: PartService;
  private readonly alertDialog: CgpAlertDialogService;
  private readonly uploadService: UploadService;
  filter: FilterDescriptor;
  invalidFilterClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  validFilterClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  submitClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  data: any[];
  editForm: FormGroup;

  constructor(private readonly injector: Injector) {
    super();
    this.controls.excel = false;
    this.controls.add = false;
    this.controls.edit = false;
    this.controls.delete = false;
    this.controls.upload = true;
    this.formBuilder = injector.get(FormBuilder);
    this.partService = injector.get(PartService);
    this.alertDialog = injector.get(CgpAlertDialogService);
    this.uploadService = injector.get(UploadService);
    this.controls.customToolBarButtons = this.partGridCustomToolBarButtons;
    this.setAllowedUploadExtensions(['.xlsx']);
    this.detailTemplate = new InvalidColumnsGrid(injector);
    this.editForm = this.formBuilder.group({});
  }

  read(opts: any): Promise<any> {
    this.data = [];
    if (this.uploadFiles.length > 0) {
      const file: File = this.uploadFiles[0].rawFile;
      const formData: FormData = new FormData();
      formData.append('file', file);
      return this.partService.uploadExcel(formData, true, '/UploadPart', true)
        .then(response => this.mapResponseData(response));
    } else {
      return Promise.resolve(this.data);
    }
  }


  private mapResponseData(response) {
    if (response) {
      response.forEach(row => {
        if (row) {
          const newRow = {
            RowIndex: row.RowIndex,
            ResponseStatus: row.ResponseStatus,
            ColumnDtos: {},
            InvalidColumns: [],
            TempColumns: []
          }
          newRow.TempColumns = row.ColumnDtos;

          row.ColumnDtos.forEach(column => {
            newRow.ColumnDtos[column.ColumnName] = column.ColumnValue;

            if (column.ErrorMessage != null) {
              newRow.InvalidColumns.push({
                [column.ColumnName]: column.ColumnValue,
                ErrorMessage: column.ErrorMessage
              })
            }
          });

          this.data.push(newRow);
        }
      });
    }
    return this.data;
  }

  create(parentEditDataItem: any): void {
  }

  edit(dataItem: any): void {
  }

  save(dataItem: any, isNew: boolean): Promise<any> {
    return Promise.resolve([]);
  }

  handleError = (error: Response) => {
    const _body = error.json();
    this.alertDialog.showAlertDialog({
      title: 'Upload Error: ',
      message: _body,
      alert: 'danger',
      closeLabel: 'Close',
    });
  }

  delete(): Promise<any> {
    return Promise.resolve([]);
  }

  /**
   * 
   * @param context 
   * callback function to add 'highlight' html class to a row
   */
  rowCallback = (context: RowClassArgs) => {
    const invalid = context.dataItem.ResponseStatus === 'INVALID';
    return {
      highlight: invalid,
    };
  }

  filterInvalid = () => {
    if (this.invalidFilterClick.value === false) {
      this.filter = {
        operator: 'eq',
        field: 'ResponseStatus',
        value: 'INVALID'
      };

      const filterArray = [this.filter];

      const compositDescriptor: CompositeFilterDescriptor = {
        logic: 'or',
        filters: filterArray,
      }
      this.gridState.filter = compositDescriptor;
      this.gridState.filter.filters = [this.filter];
      this.setGridData();
      this.invalidFilterClick.next(true);
      this.validFilterClick.next(false);
    }
    else {
      this.gridState.filter = undefined;
      this.setGridData();
      this.invalidFilterClick.next(false);
    }
  }

  filterValid = () => {
    if (this.validFilterClick.value === false) {
      this.filter = {
        operator: 'eq',
        field: 'ResponseStatus',
        value: 'VALID'
      };

      const filterArray = [this.filter];

      const compositDescriptor: CompositeFilterDescriptor = {
        logic: 'or',
        filters: filterArray,
      }
      this.gridState.filter = compositDescriptor;
      this.gridState.filter.filters = [this.filter];
      this.setGridData();
      this.validFilterClick.next(true);
      this.invalidFilterClick.next(false);
    }
    else {
      this.gridState.filter = undefined;
      this.setGridData();
      this.validFilterClick.next(false);
    }
  }

  /**
   * condition whether or not to show the detail for a row
   */
  showGridDetailCondition = (dataItem: any) => {
    return dataItem.ResponseStatus === 'INVALID';
  };


  handleSubmit = () => {
    this.uploadService.showProgramSelect.next(true);
  }

  isDisabled = (id: string): boolean => {
    const allInvalidData = this.data.some(x => x.ResponseStatus === "INVALID") && !this.data.some(x => x.ResponseStatus === "VALID");
    const emptyData = this.data.length === 0;
    return id === "Submit" && (emptyData || allInvalidData);
  }

  columns: any[] = [
    {
      field: 'RowIndex',
      title: 'Row',
    },
    {
      field: 'ColumnDtos.CSPC',
      title: 'Part Code',
    },
    {
      field: 'ColumnDtos.Part Number',
      title: 'Part Number',
    },
    {
      field: "ColumnDtos.Part Name",
      title: "Part Name",
    },
    {
      field: "ColumnDtos.Plant",
      title: "Plant",
    },
    {
      field: "ColumnDtos.Press Line",
      title: "Press Line",
    },
    {
      field: "ColumnDtos.Material",
      title: "Part Material",
    },
    {
      field: "ColumnDtos.Weight",
      title: "Part Weight",
    },
    {
      field: "ColumnDtos.Length",
      title: "Part Length",
    },
    {
      field: "ColumnDtos.Width",
      title: "Width",
    },
    {
      field: "ColumnDtos.Thickness",
      title: "Thickness",
    },
    {
      field: "ColumnDtos.Make Purchase",
      title: "Make/Purchase",
    },
    {
      field: "ColumnDtos.Program Assembly Plant",
      title: "Program Assembly Plant",
    },
    {
      field: "ColumnDtos.Type",
      title: "Type",
    },
    {
      field: "ColumnDtos.Teamcenter Image URL",
      title: "Teamcenter Image URL",
    },
    {
      field: "ColumnDtos.Toolset ID",
      title: "Toolset ID",
    },
    {
      field: "ColumnDtos.Material Specification",
      title: "Material Specification",
    },
    {
      field: "ColumnDtos.UPC",
      title: "UPC",
    },
    {
      field: "ColumnDtos.FNA",
      title: "FNA",
    },
    {
      field: "ColumnDtos.Container ID",
      title: "Container ID",
    },
    {
      field: "ColumnDtos.Container Type",
      title: "Container Type",
    },
    {
      field: "ColumnDtos.Container Style",
      title: "Container Style",
    },
    {
      field: "ColumnDtos.Parts Per Container",
      title: "Parts Per Container",
    },
    {
      field: "ColumnDtos.Container Length",
      title: "Container Length",
    },
    {
      field: "ColumnDtos.Container Width",
      title: "Container Width",
    },
    {
      field: "ColumnDtos.Container Height",
      title: "Container Height",
    },
    {
      field: "ColumnDtos.Load Side",
      title: "Load Side",
    },
    {
      field: "ColumnDtos.Container Teamcenter Image URL",
      title: "Container Teamcenter Image URL",
    },
    {
      field: "ColumnDtos.Manual Dunnage",
      title: "Manual Dunnage",
    },
    {
      field: "ColumnDtos.Parts Per Stroke",
      title: "Parts Per Stroke",
    },
    {
      field: "ColumnDtos.Die Family",
      title: "Die Family",
    },
    {
      field: "ColumnDtos.SPM Deviation",
      title: "SPM Deviation",
    },
    {
      field: "ColumnDtos.Source of Die",
      title: "Source of Die",
    },
    {
      field: "ColumnDtos.GBOM",
      title: "GBOM",
    },
    {
      field: "ColumnDtos.GBOP",
      title: "GBOP",
    },
    {
      field: "ColumnDtos.GBOP Note",
      title: "GBOP Note",
    },
    {
      field: "ColumnDtos.Tooling Restriction",
      title: "Tooling Restriction",
    },
    {
      field: "ColumnDtos.Tooling Restriction Note",
      title: "Tooling Restriction Note",
    },
    {
      field: "ColumnDtos.Reference Part Number",
      title: "Reference Part Number",
    },
    {
      field: "ColumnDtos.Reference Code",
      title: "Reference Code",
    },
    {
      field: "ColumnDtos.Reference SPM",
      title: "Reference SPM",
    },
    {
      field: "ColumnDtos.Reference Program",
      title: "Reference Program",
    },
    {
      field: "ColumnDtos.Tooling Reference Plant",
      title: "Tooling Reference Plant",
    },
    {
      field: "ColumnDtos.Tooling Reference Press",
      title: "Tooling Reference Press",
    },
    {
      field: "ColumnDtos.Parts Per Lift",
      title: "Parts Per Lift",
    },
    {
      field: "ColumnDtos.People Per Lift",
      title: "People Per Lift",
    },
    {
      field: "ColumnDtos.Walk Distance",
      title: "Walk Distance",
    },
    {
      field: "ColumnDtos.Sorting Displacement",
      title: "Sorting Displacement",
    },
    {
      field: "ColumnDtos.People Loading",
      title: "People Loading",
    },
    {
      field: "ColumnDtos.People Sorting",
      title: "People Sorting",
    },
    {
      field: "ColumnDtos.Load Assist Needed",
      title: "Load Assist Needed",
    },
    {
      field: "ColumnDtos.Manual Sorting Needed",
      title: "Manual Sorting Needed",
    },
    {
      field: "ColumnDtos.Utilized Workstations",
      title: "Utilized Workstations",
    },
    {
      field: "ColumnDtos.Presentation",
      title: "Presentation",
    },
    {
      field: "ColumnDtos.Travel Time",
      title: "Travel Time",
    },
    {
      field: "ColumnDtos.Truck Access",
      title: "Truck Access",
    },
    {
      field: "ColumnDtos.Available Trucks",
      title: "Available Trucks",
    },
    {
      field: "ColumnDtos.Container Exchange Equipment",
      title: "Container Exchange Equipment",
    },
    {
      field: "ColumnDtos.Container Limits",
      title: "Container Limits",
    },
    {
      field: "ColumnDtos.Containers Per Trip",
      title: "Containers Per Trip",
    },
    {
      field: "ColumnDtos.Orientation in 1st Tool",
      title: "Orientation in 1st Tool",
    },
    {
      field: "ColumnDtos.Orientation On EOL",
      title: "Orientation On EOL",
    },
    {
      field: "ColumnDtos.Orientation In Die",
      title: "Orientation In Die",
    },
    {
      field: "ColumnDtos.Orientation in Container",
      title: "Orientation in Container",
    },
    {
      field: "ColumnDtos.Loading Method",
      title: "Loading Method",
    },
    {
      field: "ColumnDtos.Unloading Method",
      title: "Unloading Method",
    },
    {
      field: "ColumnDtos.Distribution Method",
      title: "Distribution Method",
    },
    {
      field: "ColumnDtos.Orientation Checked",
      title: "Orientation Checked",
    },

  ]

  partGridCustomToolBarButtons = [
    {
      group: true,

      buttons: [{
        id: 'filterInvalid',
        title: 'Filter Invalid',
        text: 'Filter Invalid',
        iconClass: 'fa fa-w fa-filter',
        callback: this.filterInvalid,
        togglable: true,
        isSelected: (): boolean => this.invalidFilterClick.value,


      },
      {
        id: 'filterValid',
        title: 'Filter Valid',
        text: 'Filter valid',
        iconClass: 'fa fa-w fa-filter',
        callback: this.filterValid,
        togglable: true,
        isSelected: (): boolean => this.validFilterClick.value
      },
      {
        id: 'Submit',
        title: 'Submit',
        text: 'Submit',
        iconClass: 'fa fa-w fa-check',
        callback: this.handleSubmit,
        togglable: true,
        isSelected: (): boolean => this.submitClick.value
      },],
    }]

}
